import { Form, Button, Col, Row, Space } from 'antd';
import { useState } from 'react';
import useOnFetch from '../../hooks/useOnFetch';
import NotifResult from '../global/NotifResult';
import useTranslations from '../../hooks/useTranslations';
import useFetchQuery from '../../hooks/useFetchQuery';

/**
 * @typedef {object} FormSubmitProps
 * @property {React.ReactNode} children
 * @property {string} formAction
 * @property {string} [editAction]
 * @property {object} [customPayload]
 * @property {(formData: object, formAction: string) => object} [beforeSubmit]
 * @property {import("antd").FormInstance} form
 * @property {(data: object) => void} [onSuccess]
 * @property {boolean} [showButtonSubmit]
 * @property {string} [dataSource]
 * @property {object} [params]
 * @property {(formData : object)=> void} [customFetchResult]
 * @property {(import("react").SetStateAction)} [setLoadingSubmit]
 * @returns {JSX.Element}
 */

/**
 * @param {FormSubmitProps & import('antd').FormProps} props
 * @returns {JSX.Element}
 */
function FormSubmit({
    children,
    formAction,
    editAction,
    customPayload,
    form,
    onSuccess,
    showButtonSubmit = true,
    dataSource,
    params,
    customFetchResult,
    setLoadingSubmit,
    onFinish,
    ...props
}) {
    const fetch = useOnFetch();
    const { t } = useTranslations();

    const [loading, setIsLoading] = useState(false);
    const textSubmit = t('Submit', 'Submit');
    const textUpdate = t('Update', 'Update');
    const textSuccessAddData = t('SuccessAddData', 'Success add data');
    const textSuccessEditData = t('SuccessEditData', 'Success edit data');
    const textFailed = t('Failed', 'Failed');
    const textFailedAddData = t('FailedAddData', 'Failed add data');
    const textFailedEditData = t('FailedEditData', 'Failed edit data');
    const textSuccess = t('Success', 'Success');

    const handleSubmit = (formData) => {
        if (setLoadingSubmit) setLoadingSubmit(true);
        setIsLoading(true);
        let beforeSubmitData = {};
        if (props?.beforeSubmit) {
            beforeSubmitData = props?.beforeSubmit(formData, formAction);
        }

        const payloadData = {
            ...formData,
            ...(Object.keys(beforeSubmitData || {}).length > 0 && {
                ...beforeSubmitData,
            }),
        };

        fetch({
            url: editAction || formAction,
            method: editAction ? 'put' : 'post',
            data: {
                ...payloadData,
                ...(Object.keys(customPayload || {}).length > 0 && {
                    ...customPayload,
                }),
            },
        })
            .then((data) => {
                if (data?.code === 0) {
                    NotifResult({
                        type: 'success',
                        message: textSuccess,
                        description: editAction ? textSuccessEditData : textSuccessAddData,
                    });
                    if (onSuccess) onSuccess(data);
                } else {
                    NotifResult({
                        type: 'error',
                        message: textFailed,
                        description: editAction ? textFailedEditData : textFailedAddData,
                    });
                }
            })
            .catch(() => {
                NotifResult({
                    type: 'error',
                    message: textFailed,
                    description: editAction ? textFailedEditData : textFailedAddData,
                });
            })
            .finally(() => {
                setIsLoading(false);
                if (setLoadingSubmit) setLoadingSubmit(false);
            });
    };

    const { isLoading } = useFetchQuery({
        url: dataSource,
        enabled: () => dataSource,
        method: 'get',
        params,
        onFetchSuccess: (data) => {
            if (customFetchResult) {
                const result = customFetchResult(data);
                form.setFieldsValue(result);
            }
        },
    });

    return (
        <>
            <Form {...props} form={form} onFinish={onFinish || handleSubmit}>
                {children}
            </Form>

            {showButtonSubmit ? (
                <Row
                    justify="end"
                    style={{
                        borderTop: '1px solid #f0f0f0',
                        marginTop: 16,
                        paddingTop: 16,
                    }}
                >
                    <Col
                        span={24}
                        className="button-group"
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Space size={[8, 8]} direction="horizontal" wrap>
                            <Button type="primary" loading={loading || isLoading} onClick={() => form.submit()}>
                                {editAction ? textUpdate : textSubmit}
                            </Button>
                        </Space>
                    </Col>
                </Row>
            ) : null}
        </>
    );
}

export default FormSubmit;
